// File: frontend/src/pages/Socrates/components/AIAgentsAdmin/index.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShield, faCog, faDatabase, 
  faKey, faRobot 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAIAgents } from '../../contexts/AIAgentsContext';
import AdminDashboard from './AdminDashboard';
import AgentArtifacts from './AgentArtifacts';
import AgentVariables from './AgentVariables';
import AgentAccessControl from './AgentAccessControl';
import LoadingSpinner from '../common/LoadingSpinner';
import styles from './AIAgentsAdmin.module.css';

const AIAgentsAdmin = ({ agentName }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    agents, 
    selectedAgent,
    loading, 
    error, 
    loadAgents, 
    selectAgent,
    clearSelectedAgent 
  } = useAIAgents();
  
  const [activeTab, setActiveTab] = useState('dashboard');

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Handle URL routing and agent selection
  useEffect(() => {
    const path = location.pathname;
    
    if (agentName && agents.length > 0) {
      selectAgent(agentName);
      
      // Determine active tab from URL
      if (path.includes('/artifacts')) {
        setActiveTab('artifacts');
      } else if (path.includes('/variables')) {
        setActiveTab('variables');
      } else if (path.includes('/access_control')) {
        setActiveTab('access_control');
      } else {
        setActiveTab('dashboard');
      }
    } else if (!agentName) {
      clearSelectedAgent();
      setActiveTab('dashboard');
    }
  }, [agentName, agents, location.pathname, selectAgent, clearSelectedAgent]);

  // Handle tab navigation
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    if (selectedAgent) {
      const basePath = `/socrates/ai-agents-admin/${selectedAgent.plato_agent_name}`;
      
      switch (tab) {
        case 'dashboard':
          navigate(basePath);
          break;
        case 'artifacts':
          navigate(`${basePath}/artifacts`);
          break;
        case 'variables':
          navigate(`${basePath}/variables`);
          break;
        case 'access_control':
          navigate(`${basePath}/access_control`);
          break;
        default:
          navigate(basePath);
      }
    } else {
      navigate('/socrates/ai-agents-admin');
    }
  };

  // Handle agent selection from dropdown
  const handleAgentSelect = (agentName) => {
    if (agentName) {
      navigate(`/socrates/ai-agents-admin/${agentName}`);
    } else {
      navigate('/socrates/ai-agents-admin');
    }
  };

  // Render active tab content
  const renderTabContent = () => {
    if (!selectedAgent) {
      return (
        <div className={styles.noAgentSelected}>
          <FontAwesomeIcon icon={faRobot} className={styles.noAgentIcon} />
          <h3>{t('no_agent_selected')}</h3>
          <p>{t('select_agent_to_manage')}</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard agent={selectedAgent} />;
      case 'artifacts':
        return <AgentArtifacts agent={selectedAgent} />;
      case 'variables':
        return <AgentVariables agent={selectedAgent} />;
      case 'access_control':
        return <AgentAccessControl agent={selectedAgent} />;
      default:
        return <AdminDashboard agent={selectedAgent} />;
    }
  };

  if (loading && agents.length === 0) {
    return <LoadingSpinner size="large" message={t('loading_ai_agents_admin')} />;
  }

  return (
    <div className={styles.aiAgentsAdmin}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faShield} className={styles.titleIcon} />
            {t('ai_agents_admin')}
          </h1>
          <p className={styles.subtitle}>{t('ai_agents_admin_description')}</p>
        </div>
        <div className={styles.headerRight}>
          <select
            value={selectedAgent?.plato_agent_name || ''}
            onChange={(e) => handleAgentSelect(e.target.value)}
            className={styles.agentSelect}
          >
            <option value="">{t('select_agent')}</option>
            {agents.map((agent) => (
              <option key={agent.plato_agent_name} value={agent.plato_agent_name}>
                {agent.display_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
        </div>
      )}

      {/* Agent Info Banner */}
      {selectedAgent && (
        <div className={styles.agentBanner}>
          <div className={styles.agentIcon}>
            <FontAwesomeIcon icon={faRobot} />
          </div>
          <div className={styles.agentInfo}>
            <h2 className={styles.agentName}>{selectedAgent.display_name}</h2>
            <p className={styles.agentId}>{selectedAgent.plato_agent_name}</p>
            <p className={styles.agentDescription}>
              {selectedAgent.description || t('no_description')}
            </p>
          </div>
          <div className={`${styles.statusBadge} ${styles[selectedAgent.status]}`}>
            {t(selectedAgent.status)}
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      {selectedAgent && (
        <div className={styles.navigation}>
          <button
            className={`${styles.navButton} ${activeTab === 'dashboard' ? styles.active : ''}`}
            onClick={() => handleTabChange('dashboard')}
          >
            <FontAwesomeIcon icon={faShield} />
            <span>{t('admin_dashboard')}</span>
          </button>
          <button
            className={`${styles.navButton} ${activeTab === 'artifacts' ? styles.active : ''}`}
            onClick={() => handleTabChange('artifacts')}
          >
            <FontAwesomeIcon icon={faCog} />
            <span>{t('artifacts')}</span>
          </button>
          <button
            className={`${styles.navButton} ${activeTab === 'variables' ? styles.active : ''}`}
            onClick={() => handleTabChange('variables')}
          >
            <FontAwesomeIcon icon={faDatabase} />
            <span>{t('variables')}</span>
          </button>
          <button
            className={`${styles.navButton} ${activeTab === 'access_control' ? styles.active : ''}`}
            onClick={() => handleTabChange('access_control')}
          >
            <FontAwesomeIcon icon={faKey} />
            <span>{t('access_control')}</span>
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AIAgentsAdmin;